// docs/64 VP3/VP5: a shop's or smithy's counter, opened by asking its keeper to see the wares. Buy
// lists the week's stock; Sell lists what the band carries in its bags, at the quarter the shop
// pays. Hovering a row (or clicking it, to keep it open) shows what the piece is: where it goes,
// who may use it and what it does, from the catalogue's own numbers (`item_card`).
import { useState } from 'react';
import { Badge, Button, OryxIcon, Panel, Segmented } from '../../components';
import { getBridge } from '../../../core/encounter/session';
import { catalogueDetails, itemDetails } from '../../../core/sim/items';
import { gradeTier, gradeWord, sellValue } from '../../../core/sim/gear';
import { ItemIcon, SetFooter, UpgradeList } from '../../inventory/ItemCard';
import { upgradedName } from '../../../core/items/upgrades';
import { catalogueItem } from '../../../core/items/catalogue';

export interface CounterGood { id: bigint; def: number; tier: number; name: string; price: number; sold: boolean }
/** docs/73 TP5: a hero's pack and the next one up, for sale at the shop (docs/71 INV5). */
export interface CounterPack { heroId: bigint; hero: string; gold: number; now: string; nowCapacity: number; next?: { name: string; capacity: number; price: number } }
export interface CounterItem {
  id: bigint; def: number; tier: number; name: string; owner: string; relic: boolean; worn: boolean;
  /** docs/112: the upgrades laid on it, and the quarter of their cost a sale gives back */
  upgrades?: number[]; refund?: number;
}
const sellFor = (i: CounterItem) => sellValue(i.def, i.tier) + (i.refund ?? 0);

const tierTone = (tier: number) => (tier >= 3 ? 'gold' : tier === 2 ? 'green' : 'grey');

/** docs/159 SB3: a basic is always on the counters that stock it (and no other counter shows one). */
export const alwaysStocked = (g: Pick<CounterGood, 'def'>): boolean => !!catalogueItem(g.def)?.staple?.length;

/** One piece in words, as the counter's details card shows it. */
function Details({ def, tier, name, foot, upgrades }: { def: number; tier: number; name: string; foot: string; upgrades?: number[] }) {
  const card = getBridge()?.itemCard(def, tier);
  const d = catalogueDetails(def) ?? (card && itemDetails(card));
  return (
    <div className="fk-counter__details" role="region" aria-label={`${name} details`} data-testid="village-good-details" data-name={name}>
      <div className="fk-counter__details-head">
        <ItemIcon def={def} tier={tier} name={name} scale={3} framed upgrades={upgrades} />
        <div>
          <b className="fk-counter__details-name">{name} <Badge tone={tierTone(gradeTier(def, tier))}>{gradeWord(def, tier)}</Badge></b>
          {d && <span className="fk-counter__details-kind">{d.kind} · {d.classes}</span>}
        </div>
      </div>
      {d ? (
        <ul className="fk-counter__details-fx">{d.effects.length ? d.effects.map((e) => <li key={e}>{e}</li>) : <li className="fk-faint">No bonuses of its own.</li>}</ul>
      ) : <span className="fk-faint">The details are still loading.</span>}
      <UpgradeList upgrades={upgrades} compact />
      <SetFooter def={def} compact />
      <span className="fk-counter__details-foot">{foot}</span>
    </div>
  );
}

export function Counter({ title, keeper, goods, items, packs, busy, payer, onBuy, onSell, onPack, onTalk, onClose }: {
  title: string; keeper?: string; goods: CounterGood[]; items: CounterItem[]; packs?: CounterPack[]; busy?: string; payer?: string;
  onBuy(g: CounterGood): void; onSell(i: CounterItem): void; onPack?(p: CounterPack): void; onTalk?(): void; onClose(): void;
}) {
  const [tab, setTab] = useState<'buy' | 'sell'>('buy');
  // docs/136: the next pack up is always on the shop's counter, above the week's goods
  const forSale = (packs ?? []).filter((p) => p.next);
  const [hover, setHover] = useState<string>();
  const [pinned, setPinned] = useState<string>();
  const shown = hover ?? pinned;
  const good = tab === 'buy' ? goods.find((g) => `g${g.id}` === shown) : undefined;
  const item = tab === 'sell' ? items.find((i) => `i${i.id}` === shown) : undefined;
  const pick = (k: string) => setPinned((p) => (p === k ? undefined : k));
  return (
    <Panel variant="solid" className="fk-talk__counter fk-counter fk-hit" label={`${title}: goods`} data-testid="village-counter" data-tab={tab}>
      <header className="fk-talk__counter-head">
        <span>{title}</span>
        <span className="fk-counter__head-actions">
          {keeper && onTalk && <Button small variant="ghost" onClick={onTalk} data-testid="village-counter-talk">Talk to {keeper}</Button>}
          <Button small variant="ghost" onClick={onClose} aria-label="Close the counter" data-testid="village-counter-close">✕</Button>
        </span>
      </header>
      <Segmented label="Buy or sell" className="fk-counter__tabs" value={tab} onChange={(v) => { setTab(v); setPinned(undefined); setHover(undefined); }}
        options={[{ value: 'buy', label: `Buy (${goods.filter((g) => !g.sold).length + forSale.length})` }, { value: 'sell', label: `Sell (${items.length})` }]} />
      {tab === 'buy' && forSale.length > 0 && (
        <ul className="fk-talk__goods" data-testid="village-packs">
          {forSale.map((p) => {
            const next = p.next!;
            const short = p.gold < next.price;
            return (
              <li key={p.heroId.toString()} className="fk-counter__row" data-testid="village-pack" data-hero={p.hero}>
                <span className="fk-counter__look">
                  <OryxIcon name="item_147" scale={2} framed />
                  <span className="fk-talk__good-name">{next.name} <Badge tone="green">{next.capacity} slots</Badge>
                    <span className="fk-counter__owner">for {p.hero} · now a {p.now.toLowerCase()} ({p.nowCapacity}) · {p.gold}g in purse</span></span>
                </span>
                <Button small primary={!short} disabled={short || !!busy} loading={busy === `pack-${p.heroId}`} title={short ? `${p.hero} has ${p.gold} gold` : `${p.hero} pays from their own purse`} onClick={() => onPack?.(p)} data-testid="village-buy-pack">{next.price}g</Button>
              </li>
            );
          })}
        </ul>
      )}
      {tab === 'buy' && (goods.length === 0 ? (forSale.length ? null : <p className="fk-faint fk-counter__empty">Nothing on the counter this week.</p>) : (
        <ul className="fk-talk__goods" onMouseLeave={() => setHover(undefined)}>
          {goods.map((s) => {
            const k = `g${s.id}`;
            const basic = alwaysStocked(s);
            return (
              <li key={k} className={`fk-counter__row${s.sold ? ' fk-talk__good--sold' : ''}${basic ? ' fk-counter__row--basic' : ''}${shown === k ? ' fk-counter__row--shown' : ''}`} data-testid="village-good" data-good={s.name} data-basic={basic || undefined} onMouseEnter={() => setHover(k)}>
                <button type="button" className="fk-counter__look" onClick={() => pick(k)} aria-expanded={pinned === k} aria-label={`What is the ${s.name}?`} data-testid="village-good-look">
                  <ItemIcon def={s.def} tier={s.tier} name={s.name} scale={2} framed />
                  <span className="fk-talk__good-name">{s.name} <Badge tone={tierTone(gradeTier(s.def, s.tier))}>{gradeWord(s.def, s.tier)}</Badge>{basic && <span className="fk-counter__owner">always in stock</span>}</span>
                </button>
                <Button small primary={!s.sold} disabled={s.sold || !!busy} loading={busy === `stock-${s.id}`} title={s.sold ? undefined : `Buy for ${payer ?? 'the payer'}`} onClick={() => onBuy(s)} data-testid="village-buy">{s.sold ? 'Sold' : `${s.price}g`}</Button>
              </li>
            );
          })}
        </ul>
      ))}
      {tab === 'sell' && (items.length === 0 ? <p className="fk-faint fk-counter__empty">Your band's bags are empty. Loot from the field lands there.</p> : (
        <ul className="fk-talk__goods" onMouseLeave={() => setHover(undefined)}>
          {items.map((i) => {
            const k = `i${i.id}`;
            const why = i.relic ? 'relics are not for sale' : i.worn ? 'take it off first (on the chart, in the hero’s inventory)' : undefined;
            return (
              <li key={k} className={`fk-counter__row${shown === k ? ' fk-counter__row--shown' : ''}`} data-testid="village-sell-row" data-item={i.name} onMouseEnter={() => setHover(k)}>
                <button type="button" className="fk-counter__look" onClick={() => pick(k)} aria-expanded={pinned === k} aria-label={`What is the ${i.name}?`}>
                  <ItemIcon def={i.def} tier={i.tier} name={i.name} relic={i.relic} scale={2} framed upgrades={i.upgrades} />
                  <span className="fk-talk__good-name">{upgradedName(i.name, i.upgrades)} <Badge tone={tierTone(gradeTier(i.def, i.tier))}>{gradeWord(i.def, i.tier, i.relic)}</Badge><span className="fk-counter__owner">{i.owner}{i.worn ? ' · worn' : ''}</span></span>
                </button>
                <Button small disabled={!!why || !!busy} loading={busy === `sell-${i.id}`} title={why ?? `Sell for ${sellFor(i)} gold${i.refund ? ` (${i.refund} of it for its upgrades)` : ''}`} onClick={() => onSell(i)} data-testid="village-sell">Sell {sellFor(i)}g</Button>
              </li>
            );
          })}
        </ul>
      ))}
      {good && <Details def={good.def} tier={good.tier} name={good.name} foot={good.sold ? 'Sold this week.' : `${good.price} gold${alwaysStocked(good) ? ' · always in stock' : ''} · sells back for ${sellValue(good.def, good.tier)}`} />}
      {item && <Details def={item.def} tier={item.tier} name={upgradedName(item.name, item.upgrades)} upgrades={item.upgrades} foot={`Carried by ${item.owner} · the shop pays ${sellFor(item)} gold`} />}
      {!good && !item && <p className="fk-faint fk-counter__hint">{forSale.length && tab === 'buy' ? 'A bigger pack carries more of what you find; each hero pays for their own. ' : ''}Hover over a piece, or click it, to see what it does.</p>}
    </Panel>
  );
}
